import { atom, useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useCallback, useEffect } from 'react'
import useMediaDevices from '~/hooks/useMediaDevices'
import useSetSinkIdPolyfill from '~/hooks/useSetSinkIdPolyfill'
import Logger from '~/services/Logger'

import { StorageKeys } from './StorageKeys'

type MicPermission = PermissionState | ''

const logger = new Logger('AudioSettings')

// Global permission state atom - single source of truth
const globalPermissionAtom = atom<MicPermission>('prompt')

// Global tracking for device queries and logging
let lastDeviceQueryTime = 0
let lastLoggedPermission: MicPermission | null = null
let permissionInitialized = false

// Export function to reset global state (for testing)
export const resetAudioSettingsGlobalState = () => {
  lastLoggedPermission = null
  lastDeviceQueryTime = 0
  permissionInitialized = false
}

// Global permission checker - runs once per app lifecycle
const initializePermissionCheck = async (
  setGlobalPermission: (state: MicPermission) => void,
) => {
  if (permissionInitialized) return
  permissionInitialized = true

  try {
    const result = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    })
    setGlobalPermission(result.state as MicPermission)

    result.addEventListener('change', () => {
      setGlobalPermission(result.state as MicPermission)
    })
  } catch {
    // Fallback for browsers that don't support permissions API
    setGlobalPermission('prompt')
  }
}

interface AudioPreferences {
  inputDeviceId: string
  outputDeviceId: string
}
const audioPreferencesAtom = atomWithStorage<AudioPreferences>(
  StorageKeys.AudioDevicePreferences,
  {
    inputDeviceId: 'default',
    outputDeviceId: 'default',
  },
)

interface UseAudioSettingsStateResponse {
  permission: MicPermission
  output: {
    devices: MediaDeviceInfo[]
    preference: [string, (deviceId: string) => void]
  }
  input: {
    devices: MediaDeviceInfo[]
    preference: [string, (deviceId: string) => void]
  }
}
const useAudioSettingsState = (): UseAudioSettingsStateResponse => {
  const [preferences, setPreferences] = useAtom(audioPreferencesAtom)
  const [globalPermission, setGlobalPermission] = useAtom(globalPermissionAtom)

  const setInputPreference = useCallback(
    (deviceId: string) => {
      setPreferences((prev) => ({
        ...prev,
        inputDeviceId: deviceId,
      }))
    },
    [setPreferences],
  )

  const setOutputPreference = useCallback(
    (deviceId: string) => {
      setPreferences((prev) => ({
        ...prev,
        outputDeviceId: deviceId,
      }))
    },
    [setPreferences],
  )

  // Initialize permission checking once globally
  useEffect(() => {
    initializePermissionCheck(setGlobalPermission)
  }, [setGlobalPermission])

  // This polyfill is required for Firefox <116 and sets the setSinkId method to a noop.
  useSetSinkIdPolyfill()

  const { mediaDevices: devices, requeryDevices: requeryMediaDevices } =
    useMediaDevices()

  // Stabilize the checkAudioAccess function with global debouncing
  const checkAudioAccess = useCallback(() => {
    const now = Date.now()

    // Global debouncing - prevent excessive device queries across all instances
    if (now - lastDeviceQueryTime < 1000) {
      return
    }

    lastDeviceQueryTime = now

    try {
      requeryMediaDevices()
    } catch {
      logger.error(
        'Audio access denied',
        new Error('Microphone access prompt was explicity denied.'),
      )
    }
  }, [requeryMediaDevices])

  // Handle permission state changes and logging
  useEffect(() => {
    // Only log if this is a new permission state globally
    if (lastLoggedPermission !== globalPermission) {
      lastLoggedPermission = globalPermission

      switch (globalPermission) {
        case 'granted':
          logger.debug('Permission granted')
          break
        case 'denied':
          logger.debug('Permission denied')
          break
        case 'prompt':
          logger.debug('Permission prompt')
          checkAudioAccess()
          break
        default:
          logger.debug(`Permission ${globalPermission}`)
          checkAudioAccess()
          break
      }
    } else if (
      globalPermission === 'prompt' ||
      (globalPermission !== 'granted' && globalPermission !== 'denied')
    ) {
      checkAudioAccess()
    }
  }, [globalPermission, checkAudioAccess])

  // Ensure the preferred audio input and output devices are available.
  // Depends on changes to the navigator.mediaDevices list of devices.
  useEffect(() => {
    const preferedInputId = preferences.inputDeviceId
    const preferedOutputId = preferences.outputDeviceId
    if (
      !devices.audioInputs.find((md) => md.deviceId === preferedInputId) &&
      devices.audioInputs.length > 0
    ) {
      setInputPreference(devices.audioInputs[0]?.deviceId ?? 'default')
    }
    if (
      !devices.audioOutputs.find((md) => md.deviceId === preferedOutputId) &&
      devices.audioOutputs.length > 0
    ) {
      setOutputPreference(devices.audioOutputs[0]?.deviceId ?? 'default')
    }
  }, [
    devices,
    preferences.inputDeviceId,
    preferences.outputDeviceId,
    setInputPreference,
    setOutputPreference,
  ])

  return {
    permission: globalPermission,
    output: {
      devices: devices.audioOutputs,
      preference: [preferences.outputDeviceId, setOutputPreference],
    },
    input: {
      devices: devices.audioInputs,
      preference: [preferences.inputDeviceId, setInputPreference],
    },
  }
}

export { useAudioSettingsState }
